import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FormAlert,
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSection,
  TableKit,
} from "@NP-Webber/crm-kit";
import { shellApi, type MailLogDto } from "../../api/shellApi";
import { toClientApiError } from "../../api/httpClient";
import { useTableLayoutPrefs } from "../../hooks/useTableLayoutPrefs";
import { EditOutlinedIcon } from "../../ui/appIcons";
import { ListPageHeader } from "../../ui/ListPageHeader";
import { ListStatStrip } from "../../ui/ListStatStrip";
import { QuietButton } from "../../ui/QuietButton";
import { TableRowActions } from "../../ui/TableRowActions";

export function MailLogPage() {
  const { prefs, savePrefs } = useTableLayoutPrefs("mail_log.list");
  const [items, setItems] = useState<MailLogDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<MailLogDto | null>(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await shellApi.listMailLog());
    } catch (e) {
      setError(toClientApiError(e).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const openMail = (row: Record<string, unknown>) => {
    const id = String(row.id ?? "");
    if (!id) return true;
    const mail = items.find((m) => m.id === id);
    if (!mail) return true;
    setEdit(mail);
    setNotes(mail.notes ?? "");
    setSaveError(null);
    return true;
  };

  const handleSave = async () => {
    if (!edit || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await shellApi.updateMailNotes(edit.id, notes);
      setEdit(null);
      await reload();
    } catch (e) {
      setSaveError(toClientApiError(e).message);
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "sentAtLabel",
        title: "תאריך",
        sortable: true,
        filterable: true,
        minWidth: 140,
      },
      {
        key: "recipientEmail",
        title: "נמען",
        sortable: true,
        filterable: true,
        minWidth: 160,
      },
      {
        key: "subject",
        title: "נושא",
        sortable: true,
        filterable: true,
        minWidth: 180,
      },
      {
        key: "status",
        title: "סטטוס",
        sortable: true,
        filterable: true,
        minWidth: 90,
      },
      {
        key: "emailType",
        title: "סוג",
        sortable: true,
        filterable: true,
        minWidth: 90,
      },
      {
        key: "actions",
        title: "פעולות",
        minWidth: 72,
        render: (_value: unknown, row: Record<string, unknown>) => (
          <TableRowActions>
            <QuietButton
              title="עריכת הערה"
              tone="accent"
              icon={<EditOutlinedIcon />}
              onClick={() => {
                openMail(row);
              }}
            />
          </TableRowActions>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openMail closes over items
    [items],
  );

  const rows = items.map((m) => ({
    ...m,
    sentAtLabel: new Date(m.sentAt).toLocaleString("he-IL"),
  }));

  const statusCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of items) {
      map.set(item.status, (map.get(item.status) ?? 0) + 1);
    }
    return map;
  }, [items]);

  const statusCards = useMemo(() => {
    const cards = [
      {
        key: null as string | null,
        label: "סה״כ",
        value: items.length,
        color: "#1976d2",
        iconName: "mail",
      },
    ];
    Array.from(statusCounts.entries()).forEach(([status, value]) => {
      cards.push({
        key: status,
        label: status,
        value,
        color:
          status === "sent" || status === "delivered"
            ? "#2e7d32"
            : status === "failed" || status === "bounced"
              ? "#c62828"
              : "#ef6c00",
        iconName: "mail",
      });
    });
    return cards;
  }, [items.length, statusCounts]);

  const filteredRows = useMemo(() => {
    if (!statusFilter) return rows;
    return rows.filter((r) => r.status === statusFilter);
  }, [rows, statusFilter]);

  return (
    <section className="page-section page-section--list">
      <ListPageHeader
        screen
        onRefresh={() => {
          void reload();
        }}
      />

      {error ? <FormAlert severity="error">{error}</FormAlert> : null}

      <ListStatStrip
        title="סיכום דיוור"
        activeKey={statusFilter}
        onSelect={setStatusFilter}
        columns={Math.min(4, Math.max(2, statusCards.length))}
        loading={loading}
        cards={statusCards}
      />

      {!loading && items.length === 0 ? (
        <FormAlert severity="info">אין רשומות דיוור עדיין.</FormAlert>
      ) : null}

      <div className="page-section__table-surface">
        <TableKit
          columns={columns}
          data={filteredRows as unknown as Array<Record<string, unknown>>}
          loading={loading}
          clientSideMode
          emptyMessage="אין רשומות דיוור"
          exportFileName="mail-log"
          showExport
          showFilters
          showColumnPicker
          showAdvancedSettings
          columnOrder={(prefs.columnOrder as string[] | undefined) ?? undefined}
          onColumnOrderChange={(next: string[]) => {
            void savePrefs({ ...prefs, columnOrder: next });
          }}
          hiddenColumnKeys={(prefs.hiddenColumnKeys as string[] | undefined) ?? undefined}
          onHiddenColumnKeysChange={(next: string[]) => {
            void savePrefs({ ...prefs, hiddenColumnKeys: next });
          }}
          onRowClick={openMail}
          onRowDoubleClick={openMail}
          onOpenCard={openMail}
        />
      </div>

      <FormDialog
        open={Boolean(edit)}
        onClose={() => {
          if (!saving) {
            setEdit(null);
            setSaveError(null);
          }
        }}
        size="md"
      >
        <FormCard
          title={edit ? `הערות — ${edit.subject}` : "הערות לדיוור"}
          error={saveError}
          onSave={() => {
            void handleSave();
          }}
          onCancel={() => {
            setEdit(null);
            setSaveError(null);
          }}
          saveLabel="שמירה"
          cancelLabel="ביטול"
          saving={saving}
        >
          <FormSection>
            <FormGrid cols={2}>
              <FormField
                label="נמען"
                value={edit?.recipientEmail ?? ""}
                onChange={() => undefined}
                disabled
              />
              <FormField
                label="סטטוס"
                value={edit?.status ?? ""}
                onChange={() => undefined}
                disabled
              />
              <FormField
                label="סוג"
                value={edit?.emailType ?? ""}
                onChange={() => undefined}
                disabled
              />
              <FormField
                label="תאריך"
                value={
                  edit?.sentAt
                    ? new Date(edit.sentAt).toLocaleString("he-IL")
                    : ""
                }
                onChange={() => undefined}
                disabled
              />
              <FormField
                label="הערה"
                type="textarea"
                rows={4}
                value={notes}
                onChange={setNotes}
                disabled={saving}
                span={2}
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>
    </section>
  );
}
